import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import filter from "lodash/filter";
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import { Stack } from "PFComponents/containers/flex";
import Hr from "PFComponents/hr";
import { ListAction, SortableList, SortableListProps } from "PFComponents/sortable_list";
import { Typography } from "PFComponents/typography";
import { useOptionalTranslation } from "PFCore/hooks/use_optional_translation";
import { TableColumn } from "PFTypes";
import { useEffect, useMemo, useState } from "react";

import css from "./table_properties.module.scss";

type TablePropertiesProps = {
  selectedColumns: TableColumn[];
  availableColumns?: TableColumn[];
  keyPrefix?: string;
  onColumnsChange: (columns: TableColumn[]) => void;
};
export const TableProperties = ({
  selectedColumns,
  availableColumns = [],
  keyPrefix,
  onColumnsChange
}: TablePropertiesProps) => {
  const { t, optionalT } = useOptionalTranslation("auditPlanner");

  // Optimistic update for immediate DnD feedback
  const [localSelectedColumns, setLocalSelectedColumns] = useState(selectedColumns);
  useEffect(() => {
    setLocalSelectedColumns(selectedColumns);
  }, [selectedColumns]);

  const visibleColumns = filter(
    localSelectedColumns,
    (column) => column.locked || find(availableColumns, ["name", column.name])
  ) as TableColumn[];

  const unselectedColumns = filter(
    availableColumns,
    (column) => !find(localSelectedColumns, ["name", column.name])
  ) as TableColumn[];

  const handleChange = (newColumns: TableColumn[]) => {
    setLocalSelectedColumns(newColumns);
    onColumnsChange(newColumns);
  };

  const handleOrderChange: SortableListProps["onChange"] = (sortedItems, _, listAction) => {
    let newColumns: TableColumn[] = [];
    if (listAction === ListAction.Sort) {
      newColumns = sortedItems.map(({ id }) => availableColumns.find(({ name }) => id === name)!);
    }

    handleChange(newColumns);
  };

  const handleToggleChange = (itemId: string, toggled: boolean) => {
    const column = availableColumns.find(({ name }) => itemId === name)!;
    const newColumns: TableColumn[] = toggled
      ? [...localSelectedColumns, column]
      : localSelectedColumns.filter(({ name }) => name !== itemId);

    handleChange(newColumns);
  };

  const selectedItems: SortableListProps["items"] = useMemo(
    () =>
      visibleColumns.map((column) => ({
        id: column.name,
        label: optionalT(column.title),
        toggled: !!localSelectedColumns.find(({ name }) => name === column.name),
        actionDisabled: column.locked,
        sortDisabled: column.fixedPosition,
        qaId: `TablePropertiesItemToggle-${column.title}`
      })),
    [localSelectedColumns, visibleColumns, optionalT]
  );

  const unselectedItems: SortableListProps["items"] = useMemo(
    () =>
      unselectedColumns.map((column) => ({
        id: column.name,
        label: optionalT(column.title),
        qaId: `TablePropertiesItemToggle-${column.title}`
      })),
    [unselectedColumns, optionalT]
  );

  return (
    <Stack gap="spacingMd" className={css.columnButtonsContainer} data-qa-id="TablePropertiesContent">
      {!isEmpty(selectedItems) && <Typography variant="h5">{t("table.visibleColumns")}</Typography>}
      <SortableList
        onChange={handleOrderChange}
        onToggle={handleToggleChange}
        items={selectedItems}
        strategy={verticalListSortingStrategy}
        action={ListAction.Toggle}
        keyPrefix={keyPrefix}
      />
      {!isEmpty(unselectedItems) && (
        <>
          <Hr className={css.hr} />
          <Typography variant="h5">{t("table.hiddenColumns")}</Typography>
        </>
      )}
      <SortableList
        onChange={() => {}}
        onToggle={handleToggleChange}
        items={unselectedItems}
        strategy={verticalListSortingStrategy}
        action={ListAction.Toggle}
        keyPrefix={keyPrefix}
        disabled
      />
    </Stack>
  );
};
